// packages/console/src/components/LiveWebRTCPlaybackPage.tsx
//
// The standalone WebRTC (WHEP) playback "route", the AV1 sub-second analogue of
// LivePlaybackPage. A new tab is opened at this origin with "?whep=<encoded url>"
// (see the WebRTC button in LiveEncoderPanel); App renders THIS page when that
// param is present. The WHEP URL carries its own playback token, so the tab needs
// no session. AV1-over-WebRTC decodes in Chrome/Edge; on other browsers or any
// failure it points the operator back to the HLS "Play" affordance.

import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { connectWhep, llHlsUrlFromWhep, type WhepSession } from "../api/whep";
import { whepUrlFromLocation, TITLE_PARAM } from "../lib/livePlayback";

export function LiveWebRTCPlaybackPage() {
  const url = whepUrlFromLocation(window.location.search);
  const title = new URLSearchParams(window.location.search).get(TITLE_PARAM) || "Live playback";
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [status, setStatus] = useState<"connecting" | "webrtc" | "llhls" | "failed">("connecting");

  useEffect(() => {
    if (!url) return;
    const video = videoRef.current;
    if (!video) return;
    let cancelled = false;
    let session: WhepSession | null = null;
    let hls: Hls | null = null;

    // Fall back from WebRTC to part-based LL-HLS (~2-4s) rather than the slow S3 HLS.
    const fallbackLLHLS = () => {
      if (cancelled) return;
      const llurl = llHlsUrlFromWhep(url);
      if (!Hls.isSupported()) {
        // Safari plays LL-HLS natively.
        video.src = llurl;
        setStatus("llhls");
        return;
      }
      hls = new Hls({ lowLatencyMode: true, backBufferLength: 8 });
      hls.on(Hls.Events.FRAG_BUFFERED, () => !cancelled && setStatus("llhls"));
      hls.on(Hls.Events.ERROR, (_e, d) => { if (d.fatal && !cancelled) setStatus("failed"); });
      hls.loadSource(llurl);
      hls.attachMedia(video);
    };

    connectWhep(video, url)
      .then((s) => {
        if (cancelled) { s.close(); return; }
        session = s;
        setStatus("webrtc");
      })
      .catch(() => fallbackLLHLS());
    return () => { cancelled = true; session?.close(); hls?.destroy(); };
  }, [url]);

  return (
    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", background: "var(--bg-base)", color: "var(--fg1)" }}>
      <div style={{ flex: "none", display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: "1px solid var(--line)" }}>
        <span style={{ font: "600 12px var(--font-sans)", letterSpacing: ".08em", textTransform: "uppercase", color: "var(--fg3)" }}>
          {title}
        </span>
        {(status === "webrtc" || status === "llhls") && (
          <span style={{ font: "600 9px var(--font-sans)", letterSpacing: ".08em", color: status === "webrtc" ? "var(--blue-400)" : "var(--fg3)" }}>
            {status === "webrtc" ? "⚡ WEBRTC" : "LL-HLS"}
          </span>
        )}
      </div>
      <div style={{ flex: 1, position: "relative", minHeight: 0, background: "#000" }}>
        {url ? (
          <>
            <video ref={videoRef} autoPlay playsInline muted controls style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", background: "#000" }} />
            {(status === "connecting" || status === "failed") && (
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", font: "var(--t-body-sm)", color: "var(--fg4)", background: "rgba(3,5,6,.6)", textAlign: "center", padding: 20 }}>
                {status === "connecting"
                  ? "Connecting (WebRTC, falling back to LL-HLS)…"
                  : "Playback failed. For WebRTC use Chrome/Edge; otherwise the stream may be offline."}
              </div>
            )}
          </>
        ) : (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", font: "var(--t-body-sm)", color: "var(--fg4)" }}>
            No WebRTC URL. Open this page from the live console's WebRTC button.
          </div>
        )}
      </div>
    </div>
  );
}
